import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isBalconySectionA,
	isBalconySectionB,
	isBalconyOrPorchPermitGR6_1
} from '../__index.utils'


export const STEP4_1FIELDS = {
	typesOfWorkBalcony: 'typesOfWorkBalcony',
	installationType: 'installationType',
	foundationTypeBalcony: 'foundationTypeBalcony',
	locationBalcony: 'locationBalcony',
	widthBalcony: 'widthBalcony',
	// widthUnitBalcony: 'widthUnitBalcony',
	depthBalcony: 'depthBalcony',
	// depthUnitBalcony: 'depthUnitBalcony',
	heightBalcony: 'heightBalcony',
	// heightUnitBalcony: 'heightUnitBalcony',
	areaBalcony: 'areaBalcony',
	// areaUnitBalcony: 'areaUnitBalcony',
	structureBalcony: 'structureBalcony',
	roofBalcony: 'roofBalcony',
	foundationTypeTerrace: 'foundationTypeTerrace',
	locationTerrace: 'locationTerrace',
	isTerraceInclude: 'isTerraceInclude',
	heightTerrace: 'heightTerrace',
	// heightUnitTerrace: 'heightUnitTerrace',
	areaTerrace: 'areaTerrace',
	// areaUnitTerrace: 'areaUnitTerrace',
	wallsTerrace: 'wallsTerrace',
	structureTerrace: 'structureTerrace',
	roofTerrace: 'roofTerrace'
} as const

export type Step4_1Fields = keyof typeof STEP4_1FIELDS

/**
 * useStep4_1State - Custom hook to manage the state for step 4_1 of the GR6 Land Development Work Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_1.
 */

// --- 4.1. State HOOK for Step 4.1 ---
// This hook is used to manage the state of Step 4.1 in the GR6
export function useStep4_1State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR6_1 = isBalconyOrPorchPermitGR6_1(answers?.whatToDo)

	return {
		// Quel(s) type(s) de travaux souhaitez-vous effectuer?
		typesOfWorkBalcony: {
			name: 'typesOfWorkBalcony',
			label:
				pageAssets?.request_form_residentialRenovationOrRepairPermit_typesOfWork,
			labelKey: 'request_form_residentialRenovationOrRepairPermit_typesOfWork',
			value: answers?.typesOfWorkBalcony?.toString() || '',
			required: isGR6_1,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Quel est le type d'installation?
		installationType: {
			name: 'installationType',
			label: pageAssets?.request_form_swimmingPoolSpaPermit_installationType,
			labelKey: 'request_form_swimmingPoolSpaPermit_installationType',
			value: answers?.installationType?.toString() || '',
			required:
				isGR6_1 &&
				isBalconySectionA(answers?.typesOfWorkBalcony) &&
				[
					REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.firstOption,
					REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.thirdOption
				].includes(answers?.mainUseOfExistingBuilding || ''),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Fondation
		foundationTypeBalcony: {
			name: 'foundationTypeBalcony',
			label: pageAssets?.request_form_landDevWorkPermit_foundationTypeBalcony,
			labelKey: 'request_form_landDevWorkPermit_foundationTypeBalcony',
			value: answers?.foundationTypeBalcony?.toString() || '',
			required:
				isGR6_1 &&
				isBalconySectionA(answers?.typesOfWorkBalcony) &&
				[
					REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.firstOption,
					REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.thirdOption
				].includes(answers?.mainUseOfExistingBuilding || ''),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Emplacement
		locationBalcony: {
			name: 'locationBalcony',
			label: pageAssets?.request_form_location,
			labelKey: 'request_form_location',
			value: answers?.locationBalcony?.toString() || '',
			required: isGR6_1 && isBalconySectionA(answers?.typesOfWorkBalcony),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ***** Dimensions *****
		// Largeur
		widthBalcony: {
			name: 'widthBalcony',
			label: pageAssets?.request_form_width,
			labelKey: 'request_form_width',
			value: answers?.widthBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		widthUnitBalcony: {
			name: 'widthUnitBalcony',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value:
				answers?.widthUnitBalcony?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Profondeur
		depthBalcony: {
			name: 'depthBalcony',
			label: pageAssets?.request_form_buildingTransactionRequest_depth_label,
			labelKey: 'request_form_buildingTransactionRequest_depth_label',
			value: answers?.depthBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		depthUnitBalcony: {
			name: 'depthUnitBalcony',
			label: pageAssets?.request_form_depth_unit,
			labelKey: 'request_form_depth_unit',
			value:
				answers?.depthUnitBalcony?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Hauteur
		heightBalcony: {
			name: 'heightBalcony',
			label: pageAssets?.request_form_height,
			labelKey: 'request_form_height',
			value: answers?.heightBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		heightUnitBalcony: {
			name: 'heightUnitBalcony',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.heightUnitBalcony?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie
		areaBalcony: {
			name: 'areaBalcony',
			label: pageAssets?.request_form_surfaceArea,
			labelKey: 'request_form_surfaceArea',
			value: answers?.areaBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaUnitBalcony: {
			name: 'areaUnitBalcony',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaUnitBalcony?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ***** Matériaux *****
		// Structure
		structureBalcony: {
			name: 'structureBalcony',
			label: pageAssets?.request_form_structure,
			labelKey: 'request_form_structure',
			value: answers?.structureBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		//Toit
		roofBalcony: {
			name: 'roofBalcony',
			label: pageAssets?.request_form_roof,
			labelKey: 'request_form_roof',
			value: answers?.roofBalcony?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		//*************************************************
		//******************  TERRASSE  *******************
		//*************************************************
		// ----------------------------------------------------------------
		foundationTypeTerrace: {
			name: 'foundationTypeTerrace',
			label: pageAssets?.request_form_landDevWorkPermit_foundationTypeBalcony,
			labelKey: 'request_form_landDevWorkPermit_foundationTypeBalcony',
			value: answers?.foundationTypeTerrace?.toString() || '',
			required: isGR6_1 && isBalconySectionB(answers?.typesOfWorkBalcony),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// EmplacementTerrace
		locationTerrace: {
			name: 'locationTerrace',
			label: pageAssets?.request_form_location,
			labelKey: 'request_form_location',
			value: answers?.locationTerrace?.toString() || '',
			required: isGR6_1 && isBalconySectionB(answers?.typesOfWorkBalcony),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// La terrasse comprend-elle...
		isTerraceInclude: {
			name: 'isTerraceInclude',
			label: pageAssets?.request_form_landDevWorkPermit_isTerraceInclude,
			labelKey: 'request_form_landDevWorkPermit_isTerraceInclude',
			value: answers?.isTerraceInclude?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// **** Dimensions *****
		// Hauteur
		heightTerrace: {
			name: 'heightTerrace',
			label: pageAssets?.request_form_height,
			labelKey: 'request_form_height',
			value: answers?.heightTerrace?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4,
			description: pageAssets?.request_form_heightTerrace_description
		},
		heightUnitTerrace: {
			name: 'heightUnitTerrace',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.heightUnitTerrace?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie
		areaTerrace: {
			name: 'areaTerrace',
			label: pageAssets?.request_form_surfaceArea,
			labelKey: 'request_form_surfaceArea',
			value: answers?.areaTerrace?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Superficie unit
		areaUnitTerrace: {
			name: 'areaUnitTerrace',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaUnitTerrace?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Murs (revêtement extérieur)
		wallsTerrace: {
			name: 'wallsTerrace',
			label: pageAssets?.request_form_wallsTerrace,
			labelKey: 'request_form_wallsTerrace',
			value: answers?.wallsTerrace?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// StructureTerrace
		structureTerrace: {
			name: 'structureTerrace',
			label: pageAssets?.request_form_structure,
			labelKey: 'request_form_structure',
			value: answers?.structureTerrace?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ToitTerrace
		roofTerrace: {
			name: 'roofTerrace',
			label: pageAssets?.request_form_roof,
			labelKey: 'request_form_roof',
			value: answers?.roofTerrace?.toString() || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
